import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Card, Col, Row, Statistic } from 'antd'

import { formatBaht } from './format'

const DANGER_COLOR = '#cf1322'
const SUCCESS_COLOR = '#389e0d'

const Tile = ({ footer, highlight, onClick, title, value }) => (
  <Card hoverable={!!onClick} onClick={onClick} size="small" style={{ height: '100%' }}>
    <Statistic title={title} value={value} valueStyle={highlight ? { color: highlight } : undefined} />
    {footer && <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginTop: 4 }}>{footer}</div>}
  </Card>
)

Tile.propTypes = {
  footer: PropTypes.node,
  highlight: PropTypes.string,
  onClick: PropTypes.func,
  title: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired
}

Tile.defaultProps = {
  footer: null,
  highlight: null,
  onClick: null
}

const SummaryCards = ({ isCurrentMonth, onOpenOnlinePayments, onSelectStatus, summary }) => {
  const { i18n } = useTranslation('highValueBaskets')
  const t = (key, options) => i18n.t(`highValueBaskets:${key}`, options)
  const { cashIn, minGrandTotal, staleAfterDays, totals } = summary

  return (
    <>
      <h3>{t('cashIn.heading')}</h3>
      <Row gutter={[16, 16]}>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={t('cashIn.receivedFooter', {
              transferredBack: formatBaht(cashIn.transferredBack),
              net: formatBaht(cashIn.net)
            })}
            title={t('cashIn.received')}
            value={formatBaht(cashIn.received)}
          />
        </Col>
        <Col lg={6} sm={12} xs={24}>
          <Tile footer={t('cashIn.awaitingReceiptFooter')} title={t('cashIn.awaitingReceipt')} value={formatBaht(cashIn.awaitingReceipt)} />
        </Col>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={t('cashIn.onlineFooter')}
            onClick={onOpenOnlinePayments}
            title={t('cashIn.online')}
            value={formatBaht(cashIn.onlineReceived)}
          />
        </Col>
        {isCurrentMonth && (
          <Col lg={6} sm={12} xs={24}>
            <Tile
              footer={t('cashIn.todayFooter', { amount: formatBaht(cashIn.todayAwaitingReceipt) })}
              title={t('cashIn.today')}
              value={formatBaht(cashIn.todayReceived)}
            />
          </Col>
        )}
      </Row>

      <h3 style={{ marginTop: 24 }}>
        {t('pipeline.heading', { amount: formatBaht({ value: minGrandTotal }) })}
      </h3>
      <Row gutter={[16, 16]}>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={formatBaht(totals.total)}
            onClick={() => onSelectStatus('all')}
            title={t('pipeline.total')}
            value={t('pipeline.count', { n: totals.totalCount })}
          />
        </Col>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={formatBaht(totals.open)}
            onClick={() => onSelectStatus('open')}
            title={t('pipeline.open')}
            value={t('pipeline.count', { n: totals.openCount })}
          />
        </Col>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={t('pipeline.needsFollowUpFooter', { days: staleAfterDays })}
            highlight={totals.needsFollowUpCount > 0 ? DANGER_COLOR : null}
            onClick={() => onSelectStatus('needs_follow_up')}
            title={t('pipeline.needsFollowUp')}
            value={t('pipeline.count', { n: totals.needsFollowUpCount })}
          />
        </Col>
        <Col lg={6} sm={12} xs={24}>
          <Tile
            footer={t('pipeline.wonFooter', { amount: formatBaht(totals.won), rate: totals.winRate })}
            highlight={SUCCESS_COLOR}
            onClick={() => onSelectStatus('won')}
            title={t('pipeline.won')}
            value={t('pipeline.count', { n: totals.wonCount })}
          />
        </Col>
      </Row>
    </>
  )
}

SummaryCards.propTypes = {
  isCurrentMonth: PropTypes.bool.isRequired,
  onOpenOnlinePayments: PropTypes.func.isRequired,
  onSelectStatus: PropTypes.func.isRequired,
  summary: PropTypes.object.isRequired
}

export default SummaryCards
